import { useState, useEffect, useMemo } from 'react';
import { dbSurgeries, type Surgery, type ToothRecord } from '../../lib/localDB';
import { useNavigate } from 'react-router-dom';
import AdminStats from './components/AdminStats';
import SurgeriesTable from './components/SurgeriesTable';
import BrandAnalysis from './components/BrandAnalysis';
import AdminFilters from './components/AdminFilters';
import { type SurgeryFull, type AdminAnalytics, type Filters } from './types';

export default function AdminPanel() {
  const [loading, setLoading] = useState(true);
  const [surgeries, setSurgeries] = useState<SurgeryFull[]>([]);
  const [filters, setFilters] = useState<Filters>({ gender: '', smoker: '', failed: '', dateFrom: '', dateTo: '', search: '' });
  const [activeTab, setActiveTab] = useState<'overview' | 'surgeries'>('overview');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const allSurgeries = await dbSurgeries.getAll();
      const fullSurgeries: SurgeryFull[] = allSurgeries.map((s) => ({
        ...s,
        implants: s.teeth || [],
      }));
      setSurgeries(fullSurgeries);
    } catch (e) {
      console.error('Veri yüklenemedi:', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredSurgeries = useMemo(() => {
    let result = surgeries;
    if (filters.gender) result = result.filter((s) => s.cinsiyet === filters.gender);
    if (filters.smoker === 'yes') result = result.filter((s) => s.sigara !== '0');
    else if (filters.smoker === 'no') result = result.filter((s) => s.sigara === '0');
    if (filters.failed === 'has_fail') result = result.filter((s) => s.implants.some((t) => t.failed === true));
    else if (filters.failed === 'no_fail') result = result.filter((s) => s.implants.every((t) => !t.failed));
    if (filters.dateFrom) result = result.filter((s) => s.operation_date >= filters.dateFrom);
    if (filters.dateTo) result = result.filter((s) => s.operation_date <= filters.dateTo);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((s) =>
        s.implants.some((t) => t.implant_brand.toLowerCase().includes(q)) ||
        (s.patient_name || '').toLowerCase().includes(q) ||
        (s.clinic || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [surgeries, filters]);

  const analytics: AdminAnalytics = useMemo(() => {
    const allTeeth = filteredSurgeries.flatMap((s) => s.implants);
    const failed = allTeeth.filter((t) => t.failed === true).length;
    return {
      totalSurgeries: filteredSurgeries.length,
      totalImplants: allTeeth.length,
      failedImplants: failed,
      failRate: allTeeth.length > 0 ? (failed / allTeeth.length) * 100 : 0,
    };
  }, [filteredSurgeries]);

  const brandData = useMemo(() => {
    const map: Record<string, { total: number; failed: number }> = {};
    filteredSurgeries.forEach((s) => {
      s.implants.forEach((t) => {
        const brand = t.implant_brand || 'Bilinmiyor';
        if (!map[brand]) map[brand] = { total: 0, failed: 0 };
        map[brand].total++;
        if (t.failed) map[brand].failed++;
      });
    });
    return map;
  }, [filteredSurgeries]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-shield-keyhole-line text-xl text-white"></i>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Admin Paneli</h1>
              <p className="text-xs text-gray-500">Tüm Verileri Görüntüleyin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"><i className="ri-dashboard-line"></i>Dashboard</button>
            <button onClick={() => navigate('/analytics')} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"><i className="ri-bar-chart-line"></i>Analizler</button>
          </div>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Genel Bakış</h2>
            <p className="text-sm text-gray-500 mt-1">Tüm kayıtların istatistikleri</p>
          </div>
          <div className="flex bg-white p-1 rounded-xl">
            {(['overview', 'surgeries'] as const).map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {tab === 'overview' ? 'Genel Bakış' : 'Ameliyatlar'}
              </button>
            ))}
          </div>
        </div>
        <AdminStats analytics={analytics} />
        <AdminFilters filters={filters} onFilterChange={setFilters} />
        {activeTab === 'overview' ? (
          <BrandAnalysis brandData={brandData} />
        ) : (
          <SurgeriesTable surgeries={filteredSurgeries} />
        )}
      </div>
    </div>
  );
}
